import React, { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { StatusComp } from "../../components/shared/statusComp";
import { useTheme } from "../../context/ThemeContext";
import InfoIcon from "../../images/icons/info-outline.inline.svg";
import { processForm } from "../../utils/processForm";
import CtaButton from "../shared/buttons/ctaButton";

const ContactForm = ({
  additionalButtonClass = "",
  additionalFieldContainerClass = "",
  arrowLightClass = "fill-white",
  arrowDarkClass = "stroke-white group-hover:stroke-black-lighter  ",
  arrowSize = "std",
  buttonClass = "w-full",
  buttonPreset = "secondary",
  secondaryCtaPreset = "senary",
  calendlyActive = false,
  calendlyDesc,
  calendlyLayout = "inline",
  checkboxClass = "",
  contactLabelsClass = "text-black-lighter ",
  customFormType = null,
  ctaLabel,
  downloadTitle,
  emailPlaceholder = "Email",
  expTextProcessing = false,
  expTextSuccess = false,
  expMainTitleExtraClass = "",
  firstFieldStyles = "",
  firstNamePlaceholder = "First Name",
  formHeading,
  formOuterButtonContainer = "",
  formType = "enquiry_form",
  formStatusExtraContainerClass = "",
  formStyles,
  formSubheading = "",
  formTooltipIconStyles = "fill-green-dark sm:fill-green-dark ",
  formTooltipStyles = "primary-tooltip",
  groupSchoolEnquiryFormHeadingClass = "text-lrg w-3/5 md:w-full",
  formHeadingAdditionalClass = "",
  hiddenFields = [],
  iconColor = "",
  iconPosition = "",
  inputStyles = "contact-form-input",
  labelColor = "text-green-dark",
  lastNamePlaceholder = "Last Name",
  programItineraryFile = "",
  phonePlaceholder = "Phone",
  scrollToTop = false,
  disableMainForm = false,
  secondaryCTA = false,
  secondaryCtaLink = "",
  secondaryCtaText = "",
  secondaryBodyDescription = "",
  setParentStatus = () => {},
  statusTextProcessing = false,
  statusTextSuccess = false,
  txtColor = "text-green-dark",
  additionalLinkStyles = "",
  secondaryCtaLinkStateData = {}
}) => {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm({
    defaultValues: {
      newsletter: false
    }
  });

  const { currentBrand, localeLanguage, currentSiteMeta } = useTheme();

  const [status, setStatus] = useState("");
  const [tooltipOpen, setTooltipOpen] = useState(0);
  const [prefillCalendlyData, setPrefillCalendlyData] = useState({});

  const intlNumber = currentSiteMeta?.contact_details?.international ?? "";
  const itineraryForms = [
    "itinerary_form",
    "itinerary_groups_form",
    "itinerary_under18_form",
    "ingo_brochure_download"
  ];

  const onSubmit = useCallback(
    async (data) => {
      if (scrollToTop) {
        window.scrollTo(0, 0);
      }

      const hiddenFieldsObj = hiddenFields?.flatMap((item) => {
        return { [item?.fieldName]: item?.fieldValue };
      });

      const formFields = {
        form_type: customFormType ?? formType ?? "enquiry_form",
        ...(Array.isArray(hiddenFieldsObj)
          ? Object.assign({}, ...hiddenFieldsObj)
          : {}),
        ...data
      };
      // console.debug("formFields => ", formFields);
      const prefillCalendly = {
        name: [formFields?.first_name, formFields?.last_name].join(" "),
        email: formFields?.email_address
      };

      setPrefillCalendlyData(prefillCalendly);

      setStatus("processing");
      await processForm({
        formdata: formFields,
        currentBrand
      });

      setStatus("success");

      if (
        typeof window !== "undefined" &&
        customFormType === "brochure_download"
      ) {
        window.localStorage.setItem("brochureDownloadForm", false);
      }

      reset();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      currentBrand,
      hiddenFields,
      formType,
      reset,
      localeLanguage,
      customFormType
    ]
  );

  const expTexts = {
    processing: expTextProcessing
      ? expTextProcessing
      : formType !== "testimonial" && formType !== "online_enquiry"
        ? "Hang tight!"
        : "Just a moment...",
    success: expTextSuccess
      ? expTextSuccess
      : itineraryForms.includes(formType)
        ? `<span class="text-[34px] text-wrap sm:no-br">${
            downloadTitle ?? "Itinerary"
          } download</span>`
        : formType !== "testimonial"
          ? customFormType === "openday"
            ? "You're booked!"
            : formType === "parent_request"
              ? "Success!"
              : formType === "online_enquiry"
                ? "Thank you!"
                : "We found them!"
          : "Thank you!"
  };

  const statusTexts = {
    processing: statusTextProcessing
      ? statusTextProcessing
      : itineraryForms.includes(formType)
        ? ""
        : formType !== "testimonial"
          ? customFormType === "openday"
            ? "We're reserving your spot..."
            : formType === "parent_request"
              ? "We're reaching out to start the conversation. It'll only take a moment..."
              : formType === "online_enquiry"
                ? "We're making sure we have everything we need before connecting you with one of our team members!"
                : "We’re reaching out to start the conversation. It’ll only take a moment..."
          : "We’re making sure we have everything we need before connecting you with one of our team members!",
    success: statusTextSuccess
      ? statusTextSuccess.replace(
          "INTL_NUMBER",
          `<a class="font-bold underline hover:text-green-light ${additionalLinkStyles}" href='tel:${intlNumber}' title='${intlNumber}' rel="nofollow">${intlNumber}.</a>`
        )
      : itineraryForms.includes(formType)
        ? `${
            customFormType !== "brochure_download"
              ? `<div class="tab-like">Thank you for downloading the ${
                  downloadTitle?.toLowerCase() ?? "itinerary"
                }.`
              : ""
          } Please click the link below if the download fails to start automatically. <br/><br/> <a href="${programItineraryFile}" class="font-bold underline hover:text-green-light ${additionalLinkStyles}" target="_gviblank" rel="nofollow" title="Download ${
            downloadTitle ?? "itinerary"
          } "> Download ${
            downloadTitle?.toLowerCase() ?? "itinerary"
          } </a>  <br/><br/> ${
            customFormType === "brochure_download"
              ? `One of our enrollment managers will be in touch within 12 hours. <br/>If you can’t wait to speak to us, you can call us on <a class="font-bold underline hover:text-green-light ${additionalLinkStyles}" href='tel:${intlNumber}' title='${intlNumber}' rel="nofollow">${intlNumber}.</a>`
              : `You can also call of our enrollment managers on <a class="font-bold underline hover:text-green-light" href='tel:${intlNumber}' title=${intlNumber} rel="nofollow">${intlNumber}.</a>.`
          }</div>`
        : formType !== "testimonial" || formType !== "parent_request"
          ? customFormType === "openday"
            ? `Look out for an email for further details.`
            : formType === "online_enquiry"
              ? `One of our enrollment managers will be in touch within 12 hours. If you can’t wait to speak to us, you can call us on <a class="font-bold underline hover:text-green-light ${additionalLinkStyles}" href='tel:${intlNumber}' title='${intlNumber}' rel="nofollow">${intlNumber}.</a>`
              : `Look out for an email! Our team will be in touch soon.`
          : `One of our enrollment managers will be in touch within 12 hours. If you can’t wait to speak to us, you can call us on <a class="font-bold underline hover:text-green-light ${additionalLinkStyles}" href='tel:${intlNumber}' title='${intlNumber}' rel="nofollow">${intlNumber}.</a>`
  };

  const [, setIsFocused] = useState(false);

  const handleFocus = () => {
    setIsFocused(true);
  };

  const SecondaryCTAComponent = ({
    secondaryBodyDescription = "Or, apply now to join this adventure",
    secondaryCtaText,
    buttonClass,
    secondaryCtaLink,
    secondaryCtaLinkStateData,
    secondaryCtaPreset = "senary"
  }) => {
    return (
      <div className={"flex flex-col w-full justify-center"}>
        <div
          className="text-base text-white font-light py-4"
          dangerouslySetInnerHTML={{ __html: secondaryBodyDescription }}
        />
        <CtaButton
          ctaLabel={secondaryCtaText}
          preset={secondaryCtaPreset}
          labelClass="text-base leading-genericCta font-bold"
          extraButtonStyle={`${buttonClass} p-2 mx-auto group`}
          showAsButton={false}
          showArrowIcon={false}
          to={secondaryCtaLink}
          forwardingStateData={secondaryCtaLinkStateData}
          target="_blank"
        />
      </div>
    );
  };

  useEffect(() => {
    setParentStatus(status);
  }, [setParentStatus, status]);

  if (disableMainForm && !secondaryCTA) return <></>;

  return (
    <div className={formStyles}>
      {!status ? (
        <>
          {disableMainForm ? (
            <>
              {secondaryCTA && (
                <SecondaryCTAComponent
                  secondaryBodyDescription={secondaryBodyDescription}
                  secondaryCtaLink={secondaryCtaLink}
                  secondaryCtaText={secondaryCtaText}
                  buttonClass={buttonClass}
                  secondaryCtaLinkStateData={secondaryCtaLinkStateData}
                  secondaryCtaPreset={secondaryCtaPreset}
                />
              )}
            </>
          ) : (
            <>
              {formHeading?.length > 1 && (
                <>
                  {formType !== "sign-up" &&
                    customFormType !== "group_school_enquiry" && (
                      <div
                        className={`flex flex-col w-full  ${formType === "parent_request" ? "" : "pb-6 sm:pb-12"}`}
                      >
                        <h1
                          className={`heading text-left font-body font-normal ${formHeadingAdditionalClass} ${labelColor}`}
                          dangerouslySetInnerHTML={{ __html: formHeading }}
                        />
                      </div>
                    )}
                  {(formType === "sign-up" ||
                    customFormType === "group_school_enquiry") && (
                    <div className="flex flex-col w-full pb-6 sm:pb-12">
                      <h2
                        className={`heading leading-[38px] text-left font-body font-normal ${formHeadingAdditionalClass} ${labelColor} ${
                          customFormType === "group_school_enquiry"
                            ? groupSchoolEnquiryFormHeadingClass
                            : ""
                        }`}
                        dangerouslySetInnerHTML={{ __html: formHeading }}
                      />
                    </div>
                  )}
                  {formSubheading && (
                    <div className="flex flex-col w-full pb-4">
                      <p
                        className={`text-left ${labelColor} text-xs sm:text-sm`}
                        dangerouslySetInnerHTML={{ __html: formSubheading }}
                      />
                    </div>
                  )}
                </>
              )}

              <form
                id={`${formType ?? "enquiry_form"}`}
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col justify-between items-center w-full"
              >
                <div
                  className={`flex flex-row flex-wrap sm:flex-nowrap gap-1 sm:gap-2 w-full ${additionalFieldContainerClass}`}
                >
                  <label className="w-full sm:w-1/2 relative">
                    <input
                      type="text"
                      placeholder={`${firstNamePlaceholder}*`}
                      className={`pr-11 ${inputStyles} ${
                        errors.first_name ? " !border-red-600 " : ""
                      }`}
                      onFocus={handleFocus}
                      {...register("first_name", {
                        required: true,
                        pattern: /^[^\s].*/
                      })}
                    />
                    <div
                      className={`tooltip tooltip-left contact-form w-1/10 md:w-5 absolute py-3 mx-0 md:mx-6 right-5 top-1.5 md:right-0 before:py-3 before:rounded-xxs ${formTooltipStyles} tooltip-${tooltipOpen}`}
                      data-tip="This is how we will refer to you."
                    >
                      <InfoIcon
                        onClick={() => setTooltipOpen(1 - tooltipOpen)}
                        className={` ${formTooltipIconStyles} stroke-transparent group-hover:sm:fill-dark-green ml-2 z-10`}
                      />
                    </div>
                  </label>
                  <label className="w-full sm:w-1/2">
                    <input
                      type="text"
                      placeholder={`${lastNamePlaceholder}*`}
                      className={`${inputStyles} ${
                        errors.last_name ? " !border-red-600 " : ""
                      }`}
                      {...register("last_name", {
                        required: true,
                        pattern: /^[^\s].*/
                      })}
                    />
                  </label>
                </div>

                <div
                  className={`flex flex-row flex-wrap sm:flex-nowrap gap-1 sm:gap-2 py-2 w-full ${additionalFieldContainerClass}`}
                >
                  <label className="w-full sm:w-1/2">
                    <input
                      type="number"
                      placeholder={`${phonePlaceholder}*`}
                      className={`${inputStyles} ${
                        errors.phone_number ? " !border-red-600 " : ""
                      }`}
                      {...register("phone_number", {
                        required: true,
                        pattern: /\d+/
                      })}
                    />
                  </label>
                  <label className={`w-full sm:w-1/2`}>
                    <input
                      type="text"
                      placeholder={`${emailPlaceholder}*`}
                      className={`${inputStyles} ${
                        errors.email_address ? " !border-red-600 " : ""
                      }`}
                      {...register("email_address", {
                        required: true,
                        pattern: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i
                      })}
                    />
                  </label>
                </div>

                {formType === "parent_request" && (
                  <div className="flex flex-col w-full pt-1 sm:pt-3 pb-1 sm:pb-6">
                    <p className={`pb-2 ${labelColor} hidden sm:block font-bold`}>
                      (Optional) Any questions you would like to ask ahead of
                      time?
                    </p>
                    <p className={`pb-2 ${labelColor} text-xs block sm:hidden font-bold`}>
                      (Optional) Why do you want to apply for this program?
                    </p>
                    <textarea
                      className={`${labelColor} p-2 bg-transparent form-control w-full border-green-dark/30 border rounded-md focus:outline-none`}
                      rows="3"
                      {...register("why_want_to_apply", {})}
                    />
                  </div>
                )}

                <div
                  className={`contact-labels flex flex-col justify-center pb-4 font-body text-xs ${labelColor}  w-full`}
                >
                  {(formType === "sign-up" ||
                    customFormType === "group_school_enquiry") && (
                    <p className="py-2">
                      We will send the event link to this phone number the day
                      before the event, so please ensure it is correct!
                    </p>
                  )}

                  <label
                    className={`py-2 items-center text-left gap-x-3 custom-checkbox ${contactLabelsClass}`}
                  >
                    <input
                      type="checkbox"
                      className="opacity-0 absolute"
                      value={"newsletter_signup"}
                      {...register("newsletter", {})}
                    />
                    <span className="checkmark" />I would like to sign up to the
                    World of Difference newsletter to receive updates, news and
                    special offers.
                  </label>

                  {formType !== "sign-up" &&
                    customFormType !== "group_school_enquiry" && (
                      <p className={`py-2 ${contactLabelsClass}`}>
                        By submitting this form, I consent to contact via phone
                        and email, as per World of Difference’s{" "}
                        <a
                          className={`underline hover:text-green-light ${additionalLinkStyles}`}
                          href="/privacy"
                          target="_blank"
                        >
                          Privacy Policy
                        </a>
                        .
                      </p>
                    )}

                  {(formType === "sign-up" ||
                    customFormType === "group_school_enquiry") && (
                    <p className="py-2">
                      By clicking &quot;Sign up&quot; below, I have read and
                      accepted World of Difference &apos;s{" "}
                      <a
                        className={`underline hover:text-green-light ${additionalLinkStyles}`}
                        href="/privacy"
                        target="_blank"
                      >
                        Privacy Policy
                      </a>
                      .
                    </p>
                  )}
                </div>

                <div
                  className={`button-submit flex w-full justify-end ${
                    !formOuterButtonContainer && formType === "parent_request"
                      ? "sm:w-1/3"
                      : ""
                  } ${formOuterButtonContainer}`}
                >
                  <CtaButton
                    ctaLabel={ctaLabel}
                    preset={buttonPreset}
                    labelClass="text-base leading-genericCta font-bold"
                    extraButtonStyle={`${buttonClass} p-2 flex justify-center items-center mx-auto gap-2 group ${additionalButtonClass}`}
                    showAsButton={true}
                    showArrowIcon={false}
                  />
                </div>

                {secondaryCTA && (
                  <SecondaryCTAComponent
                    secondaryCtaLink={secondaryCtaLink}
                    secondaryCtaText={secondaryCtaText}
                    buttonClass={buttonClass}
                    secondaryCtaLinkStateData={secondaryCtaLinkStateData}
                    secondaryCtaPreset={secondaryCtaPreset}
                    secondaryBodyDescription={secondaryBodyDescription}
                  />
                )}
              </form>
            </>
          )}
        </>
      ) : (
        <>
          <StatusComp
            expText={expTexts[status]}
            statusText={statusTexts[status]}
            status={status}
            txtColor={txtColor}
            iconColor={iconColor}
            formType={formType}
            iconPosition={iconPosition}
            calendlyLayout={calendlyLayout}
            calendlyDesc={calendlyDesc}
            calendlyPrefill={prefillCalendlyData}
            extraContainerClass={formStatusExtraContainerClass}
            calendlyActive={calendlyActive}
            isGold={formType === "program"}
            mainTitleExtra={expMainTitleExtraClass}
          />
          {secondaryCTA && (
            <SecondaryCTAComponent
              secondaryCtaLink={secondaryCtaLink}
              secondaryCtaText={secondaryCtaText}
              buttonClass={buttonClass}
              secondaryBodyDescription={secondaryBodyDescription}
              secondaryCtaLinkStateData={secondaryCtaLinkStateData}
            />
          )}
        </>
      )}
    </div>
  );
};

export default ContactForm;
